import AddIcon from '@mui/icons-material/Add';
import { alpha, Box, Button, Card, CardHeader, Divider, Typography, useTheme } from "@mui/material";
import { FC, useCallback, useState } from "react";
import { FunctionField, TextField } from "@/components/field";
import DateField2 from "@/components/field/DateField2";
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import Create from '@/controller/Create';
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { ListContextProvider, ShowBase, useListContext, useListController } from 'ra-core';
import CreateDataAccessToken from './CreateDataAccessToken';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';
import { DataAccessTokenDto } from '@/gen/api/encrypt';
import { RecPatch } from '@/dataProvider/tools';
import DataAccessTokenDetail from './DataAccessTokenDetail';
import { useHasPermit } from '@/authProvider/useHasPermit';
import { MANAGE_DATA_ACCESS_TOKENS } from '@/constants';
import { useResourceRegister } from '@/utils/resourceRegister';


const resource = 'settings/encryption/tokens'

const DataAccessTokenList: FC = () => {
  useResourceRegister({ name: 'users-browse' })
  
  const listCtrl = useListController({ resource, basePath: '',  })

  return (
    <ListContextProvider value={listCtrl}>
      <DataAccessTokenListBody />
    </ListContextProvider>
  )
}

const DataAccessTokenListBody: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_DATA_ACCESS_TOKENS])
  const listCtx = useListContext()
  const {refetch} = listCtx
  const [openCreate, setOpenCreate] = useState(false)
  const [toView, setToView] = useState(null as DataAccessTokenDto|null)
  const setOpenView = useCallback((open) => setToView(open), [setToView])
  
  const handlers = useDefaultSavedHandlers("Create Data Access Token")
  handlers.onSuccess.merge(refetch)

  const theme = useTheme()
  const now = new Date().toISOString()
  const rowStyles = (d:DataAccessTokenDto&RecPatch) => {
    const expired = d.tokenInvalidated || d.keyInvalidated || new Date(d.expirationTime || '').toISOString() < now
    return expired ? { backgroundColor: alpha(theme.palette.error.light, 0.05) } : {}
  }
  return (
    <>
      <Card>
        <CardHeader 
          title={
            <>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Data Access Tokens
                {/* <Tooltip title="Token Validation Keys will be used to validate user tokens from applications.">
                  <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                </Tooltip> */}
              </Box>
            </>
          }
          action={
            hasWritePermit &&
            <Button 
              variant="contained" 
              color="primary" 
              size="small" 
              startIcon={<AddIcon />}
              onClick={() => { setOpenCreate(true) }}
            >
              Create
            </Button>
          }
        />
        <Divider />
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
                // '& .description': { maxWidth: 750, display: 'inline-block', wordBreak: 'break-word' },
              }}
              rowClick={(id:any, basePath:string, rec:any) => {
                setToView(rec || null)
                return 'toggleSelection'
              }}
              rowStyle={rowStyles}
              {...listCtx}
            >
              <DateField2 source="createdTime" label="Created Time" sortable={false} showTime />
              <TextField source="databaseName" label="Target Database" sortable={false} />
              <TextField source="tokenName" label="Token Name" sortable={false} />
              <TextField source="consumerUserName" label="Consumer" sortable={false} />
              <FunctionField source="dataAccessRoles" label="Expiration" sortable={false} 
                render={(d?:DataAccessTokenDto&RecPatch) => {
                  if (!d) { return null }
                  const expired = new Date(d.expirationTime || 0).toISOString() < now
                  const invalidated = (d.tokenInvalidated || d.keyInvalidated)
                  return (
                    <Typography variant="body2" color={expired || invalidated ? 'error' : 'success'} >{
                        expired     ? 'Expired' 
                      : invalidated ? 'Invalidated' 
                      : 'Not Expired'
                    }</Typography>
                  )
                }}
              />
              <DateField2 source="expirationTime" label="Expiration Time" sortable={false} showTime />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      {hasWritePermit &&
        <Create resource={resource} basePath="" {...handlers}>
          <CreateDataAccessToken open={openCreate} setOpen={setOpenCreate} />
        </Create>}
      <ShowBase id={toView ? `${toView.databaseId}|${toView.createdTime}` : -1} resource={toView ? resource : 'noop'} basePath="" onFailure={() =>{}}>
        <DataAccessTokenDetail open={!!toView} setOpen={setOpenView} />
      </ShowBase>
    </>
  )
}

export default DataAccessTokenList;
